/**
 * Local save (plan B6).
 *
 * Versioned JSON in localStorage with pure migration functions, a two-slot rolling
 * backup, and quarantine rather than a crash when a save is unreadable. Mobile Safari
 * kills backgrounded tabs without warning, so writes happen at every meaningful boundary
 * rather than on a timer.
 */

import { STARTING_RESOURCES, type UpgradeId } from '../content/economy';
import type { Resources } from '../sim/types';

const KEY_A = 'deepwater.save.a';
const KEY_B = 'deepwater.save.b';
const KEY_SETTINGS = 'deepwater.settings';
const QUARANTINE = 'deepwater.save.corrupt';

/** Bump when the shape changes, and add a migration below. */
export const SAVE_VERSION = 3;

/**
 * v2 → v3: the campaign was cut from twelve shifts to eight (2026-07-30), so a stored
 * shift number now points at different content — or at none, which would strand the save
 * past the end of the array (the report screen would offer no next shift and the victory
 * check could never fire). Each old number maps to the shift that inherited its place:
 * cut shifts map forward to whichever survivor absorbed their role. Endless indices
 * (> 100) pass through untouched — watch numbering never changed.
 */
const V2_SHIFT_MAP: Readonly<Record<number, number>> = {
  1: 1, 2: 2, 3: 2, 4: 3, 5: 3, 6: 3, 7: 4, 8: 5, 9: 6, 10: 7, 11: 8, 12: 8,
};

export interface CampaignStats {
  shiftsCompleted: number;
  moneyEarned: number;
  hostilesDestroyed: number;
  vesselsLost: number;
}

export interface SaveGame {
  version: number;
  /** Which slot was written last, so the loader knows which is newer. */
  serial: number;
  campaign: {
    shift: number;
    resources: Resources;
    upgrades: UpgradeId[];
    /** Set once the final shift is beaten; the only thing that reveals Endless. */
    endlessUnlocked: boolean;
    /** Highest Endless watch survived — the local high score. */
    endlessBest: number;
    /**
     * Manifest items ordered between shifts, by id.
     *
     * They are cargo, not inventory: they ride in on the next shift's supply vessel and
     * only apply if she survives. Losing her costs the player what they paid for, minus
     * insurance — which is the entire reason a supply ship is worth defending.
     */
    pendingManifest: string[];
    stats: CampaignStats;
  };
  highScores: number[];
}

export interface Settings {
  volume: number;
  muted: boolean;
  quality: 'low' | 'medium' | 'high' | 'ultra' | 'auto';
  /**
   * Invert vertical aim inside the scope.
   *
   * Scoped to the optic deliberately: plenty of people who want a normal wide view want
   * an inverted one down a sight, and forcing both to match would make one of them wrong.
   * The wide view has its own flag if it is ever wanted.
   */
  invertScope: boolean;
  /** Invert vertical look in the wide HORIZON view. */
  invertLook: boolean;
  haptics: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.8,
  muted: false,
  quality: 'auto',
  invertScope: false,
  invertLook: false,
  haptics: true,
};

export function freshSave(): SaveGame {
  return {
    version: SAVE_VERSION,
    serial: 0,
    campaign: {
      shift: 1,
      resources: { ...STARTING_RESOURCES },
      upgrades: [],
      endlessUnlocked: false,
      endlessBest: 0,
      pendingManifest: [],
      stats: { shiftsCompleted: 0, moneyEarned: 0, hostilesDestroyed: 0, vesselsLost: 0 },
    },
    highScores: [],
  };
}

/**
 * Read the newer of the two slots, falling back to the older one if it fails to parse.
 * A save that cannot be read at all is moved aside rather than deleted — if it is a bug
 * on our side, the player's progress is still recoverable.
 */
export function loadSave(): SaveGame | null {
  const a = readSlot(KEY_A);
  const b = readSlot(KEY_B);

  if (a && b) return a.serial >= b.serial ? a : b;
  if (a) return a;
  if (b) return b;

  const rawA = safeGet(KEY_A);
  if (rawA) {
    try { localStorage.setItem(QUARANTINE, rawA); } catch { /* storage full; nothing to do */ }
    safeRemove(KEY_A);
    safeRemove(KEY_B);
    console.warn('[deepwater] save could not be read; quarantined and starting fresh');
  }
  return null;
}

function readSlot(key: string): SaveGame | null {
  const raw = safeGet(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SaveGame;
    return migrate(parsed);
  } catch {
    return null;
  }
}

/** Alternate slots so a write interrupted mid-flight can never destroy both copies. */
export function writeSave(save: SaveGame): void {
  save.serial++;
  save.version = SAVE_VERSION;
  const key = save.serial % 2 === 0 ? KEY_A : KEY_B;
  try {
    localStorage.setItem(key, JSON.stringify(save));
  } catch {
    console.warn('[deepwater] could not write save (storage full or blocked)');
  }
}

export function migrate(save: SaveGame): SaveGame | null {
  if (typeof save !== 'object' || save === null) return null;
  if (typeof save.version !== 'number') return null;
  if (save.version > SAVE_VERSION) return null; // written by a newer build

  /*
   * Migration is by merge rather than by a chain of per-version transforms.
   *
   * Every field added since v1 has a sensible default in `freshSave()`, so spreading a
   * fresh save under the stored one fills in whatever the old shape lacked and preserves
   * everything it had. v1 saves gain `endlessUnlocked: false` and `endlessBest: 0` for
   * free, which is exactly right: a player mid-campaign has not beaten the Kraken.
   */
  const out = { ...freshSave(), ...save };
  out.campaign = { ...freshSave().campaign, ...save.campaign };
  out.campaign.resources = { ...STARTING_RESOURCES, ...save.campaign?.resources };
  if (save.version < 3 && out.campaign.shift <= 100) {
    out.campaign.shift = V2_SHIFT_MAP[out.campaign.shift] ?? 1;
  }
  out.version = SAVE_VERSION;
  return out;
}

export function loadSettings(): Settings {
  const raw = safeGet(KEY_SETTINGS);
  if (!raw) return { ...DEFAULT_SETTINGS };
  try {
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY_SETTINGS, JSON.stringify(settings));
  } catch { /* non-fatal */ }
}

export function clearSave(): void {
  safeRemove(KEY_A);
  safeRemove(KEY_B);
}

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeRemove(key: string): void {
  try { localStorage.removeItem(key); } catch { /* non-fatal */ }
}
